<template>
  <header class="border-b border-border bg-bg-elevated shadow-sm">
    <span
      class="block h-px w-full bg-[linear-gradient(90deg,transparent_0%,color-mix(in_srgb,var(--color-accent)_50%,transparent)_25%,var(--color-accent)_50%,color-mix(in_srgb,var(--color-accent)_50%,transparent)_75%,transparent_100%)]"
      aria-hidden="true"
    />
    <div class="mx-auto grid min-h-16 w-full max-w-(--layout-max-width) gap-3 px-4 py-3 sm:px-6 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
      <RouterLink
        :to="{ name: APP_ROUTE_NAMES.Calendar, params: { guildId: guild.activeDiscordGuildId.value } }"
        class="group inline-flex min-w-0 items-center gap-3 rounded-md text-text-primary no-underline outline-2 outline-transparent outline-offset-2 transition-colors duration-200 focus-visible:outline-accent"
      >
        <span
          class="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-accent/40 bg-linear-to-br from-accent/18 to-accent/4 text-accent shadow-sm transition-colors duration-200 group-hover:border-accent group-hover:bg-accent/14"
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 24 24"
            width="24"
            height="24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.6"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M12 3 4 6v6c0 4.5 3.5 8 8 9 4.5-1 8-4.5 8-9V6l-8-3Z" />
            <path d="M12 8v8M8 12h8" />
          </svg>
        </span>
        <span class="inline-flex min-w-0 flex-col gap-0.5 leading-tight">
          <span class="font-display truncate text-md font-bold tracking-wide text-text-primary">
            {{ resolvedTitle }}
          </span>
          <span class="hidden text-2xs font-semibold tracking-wide text-text-muted uppercase sm:inline">
            {{ isPlanRoute ? messages.header.raidPlanning : resolvedSubtitle }}
          </span>
        </span>
      </RouterLink>

      <div class="flex min-w-0 flex-col gap-2 rounded-lg border border-border bg-bg px-2 py-2 shadow-sm sm:flex-row sm:items-center sm:justify-end md:w-auto">
        <div
          v-if="showGuildChrome"
          class="flex min-w-0 items-center gap-2 px-1 sm:border-r sm:border-border sm:pr-3"
        >
          <span class="hidden shrink-0 text-2xs font-semibold tracking-wide text-text-muted uppercase lg:inline">
            {{ isPlanRoute ? messages.header.planningGuild : messages.header.guildLabel }}
          </span>
          <GuildSelector
            :model-value="guild.activeDiscordGuildId.value"
            :available="guild.availableGuilds.value"
            :locked="guild.lockedGuilds.value"
            :current-user-id="session?.discordUserId ?? ''"
            :loading="guild.loading.value"
            :requesting-guild-id="requestingGuildId"
            :request-error="accessRequestError"
            :bot-invite-url="botInviteUrl"
            :bot-invite-loading="botInviteLoading"
            :bot-invite-error="botInviteError"
            @update:model-value="onSelectGuild"
            @request-access="onRequestAccess"
          />
        </div>

        <div class="inline-flex shrink-0 items-center px-1">
          <span v-if="sessionLoading" class="inline-flex h-8 w-20 animate-pulse rounded-md bg-bg-surface" />
          <ProfileChip
            v-else
            :session="session"
            :open="popoverOpen"
            @toggle="popoverOpen = !popoverOpen"
            @logout="onLogout"
          />
        </div>
      </div>

      <ProfilePopover
        :open="popoverOpen"
        :session="session"
        :on-close="() => (popoverOpen = false)"
        :on-logout="onLogout"
      />
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'

import { GuildSelector } from '@/shared/ui'
import ProfileChip from '@/app/layout/ProfileChip.vue'
import ProfilePopover from '@/app/layout/ProfilePopover.vue'
import { APP_ROUTE_NAMES } from '@/shared/constants/routes'
import { FEATURE_ROUTE_NAMES } from '@/features/gvg-planner/constants/routes'
import { useGuild } from '@/shared/ports/guild'
import { logout as doLogout, session, sessionLoading } from '@/app/session'
import { useBotInvite } from '@/app/botInvite'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { appShellEn } from './locales/en.ts'
import { appShellRu } from './locales/ru.ts'

export interface AppHeaderProps {
  /**
   * Optional override for the brand title. When omitted, the locale-aware
   * catalog entry is rendered.
   */
  readonly title?: string
  /**
   * Optional override for the brand subtitle. When omitted, the
   * locale-aware catalog entry is rendered.
   */
  readonly subtitle?: string
}

const props = defineProps<AppHeaderProps>()

const route = useRoute()
const router = useRouter()
const guild = useGuild()

const catalog = defineLocaleCatalog({ en: appShellEn, ru: appShellRu })
const messages = useLocaleMessages(catalog)

const resolvedTitle = computed<string>(() => props.title ?? messages.value.header.title)
const resolvedSubtitle = computed<string>(() => props.subtitle ?? messages.value.header.subtitle)

const popoverOpen = ref<boolean>(false)
const requestingGuildId = ref<string | null>(null)
const accessRequestError = ref<string | null>(null)
const isPlanRoute = computed<boolean>(() => route.name === FEATURE_ROUTE_NAMES.GvgPlanner)
const showGuildChrome = computed<boolean>(() => session.value !== null)
const {
  inviteUrl: botInviteUrl,
  loading: botInviteLoading,
  error: botInviteError,
  fetch: fetchBotInvite,
} = useBotInvite()

watch(
  () => session.value?.discordUserId ?? null,
  (discordUserId) => {
    if (discordUserId !== null) void fetchBotInvite()
  },
  { immediate: true },
)

// Phase 7.4 — `loadAvailableGuilds()` is bootstrapped in
// `installGvgPlanner()` at app boot. The host chrome used to call it
// in `onMounted`; that bootstrap moved up one layer so the planner's
// install-time cascade is wired before any chrome paints.

async function onSelectGuild(snowflake: string): Promise<void> {
  accessRequestError.value = null
  await router.push({
    name: APP_ROUTE_NAMES.Calendar,
    params: { guildId: snowflake },
  })
}

async function onRequestAccess(snowflake: string): Promise<void> {
  if (requestingGuildId.value !== null) return
  requestingGuildId.value = snowflake
  accessRequestError.value = null
  try {
    const requested = await guild.requestGuildAccess(snowflake)
    if (!requested) {
      accessRequestError.value = messages.value.header.accessRequestFailed
    }
  } catch (cause: unknown) {
    accessRequestError.value = cause instanceof Error ? cause.message : String(cause)
  } finally {
    requestingGuildId.value = null
  }
}

async function onLogout(): Promise<void> {
  await doLogout()
}
</script>
